import {
  useCallback,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useRef,
} from 'react';
import * as Yup from 'yup';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import {
  textFieldValidation,
  businessHoursValidation,
} from '@/utils/validations';
import { KeenIcon, Modal } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import OpeningHours from './OpeningHours';
import { ModalProps } from '@mui/base/Modal';
import { IPOIData, IPOICategory } from '@/types/poi-management.types';
import { Tooltip } from 'react-tooltip';
import MediaUpload from '../habor-management/MediaUpload';
import PreviewModal from './PreviewModal';
import { useSnackbar } from 'notistack';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import NumberInput from '@/components/inputControls/NumberInput';

interface PoiModalProps extends Omit<ModalProps, 'children' | 'onSubmit'> {
  mode: 'view' | 'edit' | 'create';
  formData: Partial<IPOIData>;
  setFormData: Dispatch<SetStateAction<Partial<IPOIData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (translations?: Record<string, Partial<IPOIData>>) => Promise<void>;
  isSubmitting?: boolean;
  onTranslationsGenerated?: (
    translations: Record<string, Partial<IPOIData>>
  ) => void;
  categories: IPOICategory[];
  harborId?: string | null;
}

const PoiModal = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  categories,
  open,
  onClose,
  className,
  onTranslationsGenerated,
  harborId,
}: PoiModalProps) => {
  const [tagInput, setTagInput] = useState('');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [showPreviewMessage, setShowPreviewMessage] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const requiredFields: (keyof IPOIData)[] = [
    'name',
    'latitude',
    'longitude',
    'description',
  ];

  // Check if all required fields are filled
  useEffect(() => {
    const allRequiredFieldsFilled = requiredFields.every((field) => {
      if (field === 'latitude' || field === 'longitude') {
        return formData[field] !== undefined && formData[field] !== null;
      }
      return !!formData[field];
    });

    const noErrors = Object.keys(errors).length === 0;

    setShowPreviewMessage(allRequiredFieldsFilled && noErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, errors]);

  // Focus management for modal
  useEffect(() => {
    if (open && mode !== 'view') {
      let attempts = 0;
      let timer: NodeJS.Timeout;

      const tryFocus = () => {
        if (nameInputRef.current && attempts < 10) {
          nameInputRef.current.focus();
          // Check if focus was successful
          if (document.activeElement === nameInputRef.current) {
            return; // Success, stop trying
          }
          attempts++;
          timer = setTimeout(tryFocus, 100);
        }
      };

      // Start trying to focus after a short delay
      timer = setTimeout(tryFocus, 100);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [open, mode]);

  const createValidationSchema = useCallback(() => {
    return Yup.object().shape({
      name: textFieldValidation(
        intl ? intl.formatMessage({ id: 'POI.FORM.NAME' }) : 'POI Name',
        2,
        100,
        true,
        intl
      ),
      category_id: Yup.string()
        .typeError(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.CATEGORY_REQUIRED' })
            : 'Category is required'
        )
        .required(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.CATEGORY_REQUIRED' })
            : 'Category is required'
        ),
      subtype_id: Yup.string()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        )
        .when('category_id', {
          is: (categoryId: string) => !!categoryId,
          then: (schema) =>
            schema.required(
              intl
                ? intl.formatMessage({ id: 'POI.VALIDATION.SUBTYPE_REQUIRED' })
                : 'Subtype is required'
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      latitude: Yup.number()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }

          if (typeof originalValue === 'string') {
            const normalized = originalValue
              .replace(/\s/g, '')
              .replace(',', '.');
            const parsed = Number(normalized);
            return Number.isNaN(parsed) ? NaN : parsed;
          }

          return value;
        })
        .typeError(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_NUMBER' })
            : 'Latitude must be a number'
        )
        .min(
          -90,
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_RANGE' })
            : 'Latitude must be between -90 and 90'
        )
        .max(
          90,
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_RANGE' })
            : 'Latitude must be between -90 and 90'
        )
        .required(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LATITUDE_REQUIRED' })
            : 'Latitude is required'
        ),
      longitude: Yup.number()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }

          if (typeof originalValue === 'string') {
            const normalized = originalValue
              .replace(/\s/g, '')
              .replace(',', '.');
            const parsed = Number(normalized);
            return Number.isNaN(parsed) ? NaN : parsed;
          }

          return value;
        })
        .typeError(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_NUMBER' })
            : 'Longitude must be a number'
        )
        .min(
          -180,
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_RANGE' })
            : 'Longitude must be between -180 and 180'
        )
        .max(
          180,
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_RANGE' })
            : 'Longitude must be between -180 and 180'
        )
        .required(
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.LONGITUDE_REQUIRED' })
            : 'Longitude is required'
        ),
      description: Yup.string()
        .max(
          1000,
          intl
            ? intl.formatMessage(
                { id: 'POI.VALIDATION.DESCRIPTION_MAX' },
                { count: 1000 }
              )
            : 'Description cannot exceed 1000 characters'
        )
        .notRequired(),
      tags: Yup.array()
        .of(
          Yup.string().max(
            50,
            intl
              ? intl.formatMessage(
                  { id: 'POI.VALIDATION.TAG_MAX' },
                  { count: 50 }
                )
              : 'Tag cannot exceed 50 characters'
          )
        )
        .max(
          10,
          intl
            ? intl.formatMessage(
                { id: 'POI.VALIDATION.TAGS_MAX' },
                { count: 10 }
              )
            : 'Maximum 10 tags allowed'
        )
        .notRequired(),
      images: Yup.array()
        .of(
          Yup.mixed().test(
            'is-file-or-url',
            intl
              ? intl.formatMessage({ id: 'POI.VALIDATION.IMAGE_INVALID' })
              : 'Must be a valid file or URL',
            (value) => {
              if (typeof value === 'string') {
                try {
                  new URL(value);
                  return true;
                } catch {
                  return false;
                }
              }
              return value instanceof File;
            }
          )
        )
        .max(
          5,
          intl
            ? intl.formatMessage(
                { id: 'POI.VALIDATION.IMAGES_MAX' },
                { count: 5 }
              )
            : 'Maximum 5 images allowed'
        )
        .notRequired(),
      contact_info: textFieldValidation(
        intl
          ? intl.formatMessage({ id: 'POI.FORM.CONTACT_INFO' })
          : 'Contact Info',
        0,
        100,
        false,
        intl
      ),
      entry_fee: Yup.number()
        .min(
          0,
          intl
            ? intl.formatMessage({ id: 'POI.VALIDATION.ENTRY_FEE_POSITIVE' })
            : 'Entry fee must be positive'
        )
        .notRequired()
        .nullable(),
      opening_hours: businessHoursValidation(),
      accessibility_notes: textFieldValidation(
        intl
          ? intl.formatMessage({ id: 'POI.FORM.ACCESSIBILITY_NOTES' })
          : 'Accessibility Notes',
        0,
        500,
        false,
        intl
      ),
    });
  }, [intl]);

  const validateField = useCallback(
    async (name: keyof IPOIData | string, value: unknown) => {
      try {
        const schema = createValidationSchema();
        await schema.validateAt(
          name,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
        return true;
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newError = err.errors[0] || 'Invalid input';
          setErrors((prev) => ({
            ...prev,
            [name]: newError,
          }));
        }
        return false;
      }
    },
    [createValidationSchema, formData, setErrors]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const requiredFields: (keyof IPOIData)[] = [
        'name',
        'category_id',
        'latitude',
        'longitude',
        'description',
      ];
      let isValid = true;

      for (const field of requiredFields) {
        const valid = await validateField(field, formData[field]);
        if (!valid) {
          isValid = false;
        }
      }

      if (!isValid) {
        enqueueSnackbar(
          intl.formatMessage({ id: 'POI.VALIDATION.FIX_ERRORS' }),
          { variant: 'error' }
        );
        return;
      }

      await onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  }, [
    formData,
    onSubmit,
    validateField,
    enqueueSnackbar,
    intl,
    requiredFields,
  ]);

  const handleOpenPreview = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleInputChange = useCallback(
    async (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      const key = name as keyof IPOIData;
      const parsedValue =
        key === 'latitude' || key === 'longitude'
          ? value === ''
            ? undefined
            : parseFloat(value)
          : value;

      if (name === 'depth') {
        const sanitized = value.replace(/[^\d.,]/g, '');
        setFormData((prev) => ({
          ...prev,
          [name]: sanitized,
        }));
        await validateField(name, sanitized);
        return;
      }

      setFormData((prev) => {
        if (prev[key] === parsedValue) return prev;
        return { ...prev, [key]: parsedValue };
      });

      await validateField(key, parsedValue);
    },
    [setFormData, validateField]
  );

  const handleInputBlur = useCallback(
    (name: string, value: unknown) => {
      validateField(name, value);
    },
    [validateField]
  );

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      const newTags = [...(formData.tags || []), tagInput.trim()];
      setFormData((prev) => ({ ...prev, tags: newTags }));
      validateField('tags', newTags);
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const newTags = (formData.tags || []).filter((tag) => tag !== tagToRemove);
    setFormData((prev) => ({ ...prev, tags: newTags }));
    validateField('tags', newTags);
  };

  const handleClearAllTags = () => {
    setFormData((prev) => ({ ...prev, tags: [] }));
    validateField('tags', []);
  };

  const handleNumberInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.addEventListener('wheel', (event) => event.preventDefault(), {
        passive: false,
      });
    },
    []
  );

  const handleNumberInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.removeEventListener('wheel', (event) => event.preventDefault());
      const { name, value } = e.target;

      if (name === 'depth') {
        validateField(name, value);
      } else {
        const parsedValue = value === '' ? undefined : parseFloat(value);
        handleInputBlur(name, parsedValue);
      }
    },
    [handleInputBlur, validateField]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="poi-modal-title"
      aria-describedby="poi-modal-description"
      disableEnforceFocus={false}
    >
      <>
        <div
          className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col overflow-y-auto max-h-[560px] custom-scrollbar"
          tabIndex={-1}
        >
          <div className="flex justify-between items-center mb-6">
            <h1
              id="poi-modal-title"
              className="text-2xl font-semibold text-gray-800"
            >
              {mode === 'create'
                ? intl.formatMessage({ id: 'POI.TITLE.CREATE' })
                : mode === 'edit'
                  ? intl.formatMessage({ id: 'POI.TITLE.EDIT' })
                  : intl.formatMessage({ id: 'POI.TITLE.VIEW' })}
            </h1>
            <div className="relative">
              <select
                id="language-select"
                className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                value={currentLanguage.code}
                onChange={(e) => {
                  const selectedLang = I18N_LANGUAGES.find(
                    (lang) => lang.code === e.target.value
                  );
                  if (selectedLang) {
                    changeLanguage(selectedLang);
                  }
                }}
                aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              >
                {I18N_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 dark:invert contrast:brightness-0 contrast:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
          <div id="poi-modal-description" className="sr-only">
            {mode === 'create'
              ? 'Form to create a new POI'
              : mode === 'edit'
                ? 'Form to edit POI details'
                : 'View POI details'}
          </div>
          <div className="flex-1 pr-2 custom-scrollbar">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Image Upload */}
              <div className="col-span-2">
                <MediaUpload
                  field="images"
                  media={formData.images || []}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  disabled={mode === 'view' || (isSubmitting as boolean)}
                  allowedTypes={['image/jpeg', 'image/jpg', 'image/png']}
                  maxFiles={5}
                  previewMode={true}
                  media_captions={formData.media_captions}
                  onMediaRemoved={(removedMedia) => {
                    if (mode !== 'edit' || removedMedia.length === 0) return;
                    setFormData((prev) => {
                      const current = prev.mediaToDelete || [];
                      const next = Array.from(
                        new Set([...current, ...removedMedia])
                      );
                      return { ...prev, mediaToDelete: next };
                    });
                  }}
                />
              </div>

              {/* Name */}
              <div className="col-span-1">
                <label
                  htmlFor="name-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.NAME' })}
                </label>
                <input
                  ref={nameInputRef}
                  id="name-input"
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  onKeyDown={preventLeadingWhitespace}
                  onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300 contrast:border-gray-300 text-gray-900 disabled:text-gray-700 dark:disabled:text-gray-200 contrast:disabled:text-gray-900 disabled:opacity-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    errors.name && 'border-red-500'
                  )}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  placeholder={intl.formatMessage({ id: 'POI.FORM.NAME' })}
                />
                {errors.name && (
                  <p
                    id="name-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="col-span-1">
                <label
                  htmlFor="category-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.CATEGORY' })}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  id="category-select"
                  name="category_id"
                  value={formData.category_id ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                      ? String(e.target.value)
                      : undefined;
                    setFormData((prev) => ({
                      ...prev,
                      category_id: value,
                      subtype_id: undefined,
                    }));
                    validateField('category_id', value);
                  }}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.category_id && 'border-red-500'
                  )}
                  aria-invalid={!!errors.category_id}
                  aria-describedby={
                    errors.category_id ? 'category-error' : undefined
                  }
                >
                  <option value="">
                    {intl.formatMessage({ id: 'POI.FORM.SELECT_CATEGORY' })}
                  </option>
                  {categories?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.label || category.label_en}
                    </option>
                  ))}
                </select>
                {errors.category_id && (
                  <p
                    id="category-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.category_id}
                  </p>
                )}
              </div>

              {/* Subcategory */}
              <div className="col-span-1">
                <label
                  htmlFor="subtype-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.SUBCATEGORY' })}
                </label>
                <select
                  id="subtype-select"
                  name="subtype_id"
                  value={formData.subtype_id ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                      ? String(e.target.value)
                      : undefined;
                    setFormData((prev) => ({ ...prev, subtype_id: value }));
                    validateField('subtype_id', value ?? null);
                  }}
                  disabled={
                    mode === 'view' ||
                    isSubmitting ||
                    !formData.category_id ||
                    !categories.find((c) => c.id === formData.category_id)
                      ?.subtypes?.length
                  }
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.subtype_id && 'border-red-500'
                  )}
                  aria-invalid={!!errors.subtype_id}
                  aria-describedby={
                    errors.subtype_id ? 'subtype-error' : undefined
                  }
                >
                  <option value="">
                    {!formData.category_id
                      ? intl.formatMessage({
                          id: 'POI.FORM.SELECT_CATEGORY_FIRST',
                        })
                      : intl.formatMessage({ id: 'POI.FORM.SELECT_SUBTYPE' })}
                  </option>
                  {categories
                    ?.find((c) => c.id === formData.category_id)
                    ?.subtypes?.map((subtype) => (
                      <option key={subtype.id} value={subtype.id}>
                        {subtype.label || subtype.label_en}
                      </option>
                    ))}
                </select>
                {errors.subtype_id && (
                  <p
                    id="subtype-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.subtype_id}
                  </p>
                )}
              </div>

              {/* Opening Hours */}
              <div className="col-span-2">
                <h2 className="text-lg font-medium text-gray-800 mb-2">
                  {intl.formatMessage({ id: 'POI.FORM.OPENING_HOURS' })}
                </h2>
                <OpeningHours
                  openingHours={formData.opening_hours}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  disabled={mode === 'view' || (isSubmitting as boolean)}
                />
              </div>

              {/* Location Information */}
              <div className="col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-1">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="latitude-input"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {intl.formatMessage({ id: 'POI.FORM.LATITUDE' })}
                        <span
                          data-tooltip-id="latitudeTooltip"
                          data-tooltip-content={intl.formatMessage({
                            id: 'POI.TOOLTIP.COORDINATES',
                          })}
                          className="ml-1 inline-flex"
                        >
                          <KeenIcon
                            type="filled"
                            icon="information"
                            className="text-gray-500 cursor-pointer"
                          />
                        </span>
                        <Tooltip
                          id="latitudeTooltip"
                          place="top"
                          className="!max-w-xs"
                        />
                      </label>
                    </div>
                    <NumberInput
                      id="latitude-input"
                      name="latitude"
                      autoComplete="off"
                      value={formData.latitude ?? ''}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={handleNumberInputBlur}
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300 contrast:border-gray-300 text-gray-900 disabled:text-gray-700 dark:disabled:text-gray-200 contrast:disabled:text-gray-900 disabled:opacity-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                        errors.latitude && 'border-red-500'
                      )}
                      aria-invalid={!!errors.latitude}
                      aria-describedby={
                        errors.latitude ? 'latitude-error' : undefined
                      }
                      placeholder={`${intl.formatMessage({ id: 'POI.FORM.LATITUDE' })} (-90 to 90)`}
                      step="0.0001"
                      min="-90"
                      max="90"
                      maxDecimals={100}
                    />
                    {errors.latitude && (
                      <p
                        id="latitude-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.latitude}
                      </p>
                    )}
                  </div>

                  <div className="col-span-1">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="longitude-input"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {intl.formatMessage({ id: 'POI.FORM.LONGITUDE' })}
                        <span
                          data-tooltip-id="longitudeTooltip"
                          data-tooltip-content={intl.formatMessage({
                            id: 'POI.TOOLTIP.COORDINATES',
                          })}
                          className="ml-1 inline-flex"
                        >
                          <KeenIcon
                            type="filled"
                            icon="information"
                            className="text-gray-500 cursor-pointer"
                          />
                        </span>
                        <Tooltip
                          id="longitudeTooltip"
                          place="top"
                          className="!max-w-xs"
                        />
                      </label>
                    </div>
                    <NumberInput
                      id="longitude-input"
                      name="longitude"
                      autoComplete="off"
                      value={formData.longitude ?? ''}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={handleNumberInputBlur}
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300 contrast:border-gray-300 text-gray-900 disabled:text-gray-700 dark:disabled:text-gray-200 contrast:disabled:text-gray-900 disabled:opacity-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                        errors.longitude && 'border-red-500'
                      )}
                      aria-invalid={!!errors.longitude}
                      aria-describedby={
                        errors.longitude ? 'longitude-error' : undefined
                      }
                      placeholder={`${intl.formatMessage({ id: 'POI.FORM.LONGITUDE' })} (-180 to 180)`}
                      step="0.0001"
                      min="-180"
                      max="180"
                      maxDecimals={100}
                    />
                    {errors.longitude && (
                      <p
                        id="longitude-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.longitude}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="col-span-2">
                <label
                  htmlFor="description-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.DESCRIPTION' })}
                </label>
                <textarea
                  id="description-input"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'textarea textarea-bordered w-full rounded-lg border-gray-300',
                    errors.description && 'border-red-500'
                  )}
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description ? 'description-error' : undefined
                  }
                  placeholder={intl.formatMessage({
                    id: 'POI.FORM.DESCRIPTION',
                  })}
                  rows={4}
                />
                {errors.description && (
                  <p
                    id="description-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Tags */}
              <div className="col-span-2">
                <label
                  htmlFor="tags-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.TAGS' })}
                </label>
                <div className="flex gap-2 mb-2">
                  <div className="relative flex-1">
                    <input
                      id="tags-input"
                      type="text"
                      value={tagInput}
                      autoComplete="off"
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        (e.preventDefault(), handleTagAdd())
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300 pr-20',
                        errors.tags && 'border-red-500'
                      )}
                      placeholder={intl.formatMessage({
                        id: 'POI.FORM.ADD_TAG',
                      })}
                    />
                    {(formData.tags || []).length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearAllTags}
                        disabled={mode === 'view' || isSubmitting}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                        title={intl.formatMessage({ id: 'POI.FORM.CLEAR_ALL' })}
                      >
                        {intl.formatMessage({ id: 'POI.FORM.CLEAR_ALL' })}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleTagAdd}
                    disabled={
                      !tagInput.trim() || mode === 'view' || isSubmitting
                    }
                    className="btn btn-sm h-11 btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
                  >
                    {intl.formatMessage({ id: 'POI.FORM.ADD_TAG' })}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(formData.tags || []).map((tag) => (
                    <div
                      key={tag}
                      className="flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium px-2.5 py-0.5 rounded space-x-[2px]"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleTagRemove(tag)}
                        disabled={mode === 'view' || isSubmitting}
                        className="text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-400"
                        aria-label={`Remove ${tag} tag`}
                      >
                        <KeenIcon
                          type="filled"
                          icon="cross"
                          className="w-3 h-3"
                        />
                      </button>
                    </div>
                  ))}
                </div>
                {errors.tags && (
                  <p className="text-red-500 text-xs mt-1" role="alert">
                    {errors.tags}
                  </p>
                )}
              </div>

              {/* Optional Details Section */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  {intl.formatMessage({ id: 'POI.FORM.OPTIONAL_DETAILS' })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <div className="col-span-1">
                    <label
                      htmlFor="contact-info-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'POI.FORM.CONTACT_INFO' })}
                    </label>
                    <input
                      id="contact-info-input"
                      type="text"
                      name="contact_info"
                      autoComplete="off"
                      value={formData.contact_info || ''}
                      onChange={handleInputChange}
                      onKeyDown={preventLeadingWhitespace}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.contact_info && 'border-red-500'
                      )}
                      aria-invalid={!!errors.contact_info}
                      aria-describedby={
                        errors.contact_info ? 'contact-info-error' : undefined
                      }
                      placeholder={intl.formatMessage({
                        id: 'POI.FORM.CONTACT_INFO_PLACEHOLDER',
                      })}
                    />
                    {errors.contact_info && (
                      <p
                        id="contact-info-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.contact_info}
                      </p>
                    )}
                  </div>

                  {/* Entry Fee */}
                  <div className="col-span-1">
                    <label
                      htmlFor="entry-fee-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'POI.FORM.ENTRY_FEE' })}
                    </label>
                    <PriceInput
                      id="entry-fee-input"
                      name="entry_fee"
                      value={formData.entry_fee ?? ''}
                      onChange={(val) => {
                        setFormData((prev) => ({ ...prev, entry_fee: val }));
                        validateField('entry_fee', val);
                      }}
                      disabled={mode === 'view' || isSubmitting}
                      inputClassName={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.entry_fee && 'border-red-500'
                      )}
                      error={errors.entry_fee}
                      placeholder={intl.formatMessage({
                        id: 'POI.FORM.ENTRY_FEE_PLACEHOLDER',
                      })}
                    />
                  </div>

                  {/* Accessibility Notes */}
                  <div className="col-span-1">
                    <label
                      htmlFor="accessibility-notes-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'POI.FORM.ACCESSIBILITY_NOTES',
                      })}
                    </label>
                    <textarea
                      id="accessibility-notes-input"
                      name="accessibility_notes"
                      value={formData.accessibility_notes || ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'textarea textarea-bordered w-full rounded-lg border-gray-300 resize-none',
                        errors.accessibility_notes && 'border-red-500'
                      )}
                      aria-invalid={!!errors.accessibility_notes}
                      aria-describedby={
                        errors.accessibility_notes
                          ? 'accessibility-notes-error'
                          : undefined
                      }
                      placeholder={intl.formatMessage({
                        id: 'POI.FORM.ACCESSIBILITY_NOTES_PLACEHOLDER',
                      })}
                      rows={3}
                    />
                    {errors.accessibility_notes && (
                      <p
                        id="accessibility-notes-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.accessibility_notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Message */}
          {showPreviewMessage && (mode === 'create' || mode === 'edit') && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-100 contrast:text-blue-100 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                  {intl.formatMessage({ id: 'POI.PREVIEW.NOTICE' })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                className="btn btn-lg bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={intl.formatMessage({
                  id: 'POI.BUTTON.SHOW_PREVIEW',
                })}
              >
                {intl.formatMessage({ id: 'POI.BUTTON.SHOW_PREVIEW' })}
              </button>
            </div>
          )}

          {(mode === 'create' || mode === 'edit') && (
            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => onClose?.({}, 'backdropClick')}
                className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label={intl.formatMessage({ id: 'POI.BUTTON.CANCEL' })}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: 'POI.BUTTON.CANCEL' })}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={
                  mode === 'create'
                    ? intl.formatMessage({ id: 'POI.BUTTON.CREATE' })
                    : intl.formatMessage({ id: 'POI.BUTTON.SAVE' })
                }
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? intl.formatMessage({ id: 'POI.BUTTON.SAVING' })
                  : mode === 'create'
                    ? intl.formatMessage({ id: 'POI.BUTTON.CREATE' })
                    : intl.formatMessage({ id: 'POI.BUTTON.SAVE' })}
              </button>
            </div>
          )}

          {/* Preview Modal */}
          <PreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            formData={formData}
            source_language={currentLanguage.code}
            onTranslationsGenerated={onTranslationsGenerated}
            harborId={harborId}
          />
        </div>
      </>
    </Modal>
  );
};

export default PoiModal;
